import React, { useEffect, useState } from "react";
import { Dropdown, Button, DatePicker } from "antd";
import { AddPrimaryIcon, CloseIcon, DeleteIcon, FilterIcon } from "../../icons";
import Select from "../select";
import "./style.scss";
import Input from "../form/input";

interface FilterDropdownRolesProps {
  tableHeaders: { label: string; value: string }[];
  optionsByColumn?: Record<string, string[]>;
  currentPage: number;
  perPage: number;
  activeTab: string;
  onChange?: (filters: {
    activeFilters: string[];
    dynamicFilters: DynamicFilter[];
    apiParams: Record<string, any>;
  }) => void;
}

interface DynamicFilter {
  column: string | null;
  condition: string | null;
  value: any;
}

// --- Filter conditions per column ---
const conditionOptionsByColumn: Record<
  string,
  { label: string; value: string }[]
> = {
  role_name: [
    { label: "Contains", value: "contain" },
    { label: " Not contains", value: "notContain" },
  ],
  role_status_flg: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
  created_date: [
    { label: "Is", value: "is" },
    { label: "Is not", value: "isNot" },
  ],
};

// --- Role status options ---
const statusOptions = [
  { label: "Active", value: 1 },
  { label: "Inactive", value: 0 },
];

// --- Safe array helper ---
const ensureArray = (val: any) => (Array.isArray(val) ? val : val ? [val] : []);

const debounce = (func: (...args: any[]) => void, delay: number) => {
  let timer: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => func(...args), delay);
  };
};

// --- Build API params for /roles/v1 ---
const buildApiParams = (
  dynamicFilters: DynamicFilter[],
  currentPage: number,
  perPage: number
) => {
  const params: Record<string, any> = {
    page: currentPage,
    perPage,
    paginate: true,
  };

  dynamicFilters.forEach((f) => {
    if (!f.column || !f.condition || !f.value) return;
    const values = ensureArray(f.value);

    switch (f.column) {
      case "role_name":
        if (f.condition === "contain") params["name_contain"] = values;
        if (f.condition === "notContain") params["name_not_contain"] = values;
        break;

      case "role_status_flg":
        if (f.condition === "is") params["status_in"] = values;
        if (f.condition === "isNot") params["status_not_in"] = values;
        break;

      case "created_date":
        if (f.condition === "is")
          params["created_date_in"] = values.map((v) =>
            typeof v === "string" ? v : v?.format("DD-MM-YYYY")
          );
        if (f.condition === "isNot")
          params["created_date_not_in"] = values.map((v) =>
            typeof v === "string" ? v : v?.format("DD-MM-YYYY")
          );
        break;
    }
  });

  return params;
};

const FilterDropdownRoles: React.FC<FilterDropdownRolesProps> = ({
  tableHeaders,
  onChange,
  optionsByColumn,
  currentPage,
  perPage,
  activeTab,
}) => {
  const [activeFilters, setActiveFilters] = useState<string[]>([]);
  const [dynamicFilters, setDynamicFilters] = useState<DynamicFilter[]>([]);
  const [visible, setVisible] = useState(false);

  // --- Update parent when filters change ---
  useEffect(() => {
    const apiParams = buildApiParams(dynamicFilters, currentPage, perPage);
    onChange?.({ activeFilters, dynamicFilters, apiParams });
  }, [activeFilters, dynamicFilters, currentPage, perPage]);

  const addFilter = () =>
    setDynamicFilters([
      ...dynamicFilters,
      { column: null, condition: null, value: [] },
    ]);

  const clearAllFilters = () => {
    setActiveFilters([]);
    setDynamicFilters([]);
  };

  // ✅ Clear all filters whenever the tab changes
  useEffect(() => {
    setActiveFilters([]);
    setDynamicFilters([]);
  }, [activeTab]);

  const deleteFilter = (index: number) =>
    setDynamicFilters(dynamicFilters.filter((_, i) => i !== index));

  const updateFilter = (index: number, key: keyof DynamicFilter, val: any) => {
    const updated = [...dynamicFilters];
    updated[index][key] = val;
    setDynamicFilters(updated);
  };

  const activeFilterCount = activeFilters.length + dynamicFilters.length;

  const renderValueSelect = (f: DynamicFilter, idx: number) => {
    if (!f.column || !f.condition) return null;

    switch (f.column) {
      case "role_status_flg":
        return (
          <Select
            options={statusOptions}
            value={f.value}
            onChange={(val) => updateFilter(idx, "value", val)}
            placeholder="Select status"
          />
        );

      case "created_date":
        return (
          <DatePicker
            value={f.value || null}
            onChange={(val) => updateFilter(idx, "value", val)}
            placeholder="Select date"
            className="w-full filter-date-picker"
          />
        );

      case "role_name":
        // Debounced input for role_name
        const handleRoleNameChange = debounce((val: string) => {
          updateFilter(idx, "value", val);
        }, 3000); // ⏳ 3 seconds delay

        return (
          <Input
            defaultValue={f.value}
            onChange={(e) => handleRoleNameChange(e.target.value)}
            placeholder="Enter role name filter_input"
            list={`options-${f.column}-${idx}`}
          />
        );

      default:
        const options = optionsByColumn?.[f.column] || [];
        return (
          <Input
            value={f.value}
            onChange={(e) => updateFilter(idx, "value", e.target.value)}
            placeholder={
              f.column === "role_name" ? "Enter role name" : "Enter value"
            }
            list={`options-${f.column}-${idx}`}
          />
        );
    }
  };

  const menu = (
    <div className="filter-dropdown">
      <div className="header-wrapper flex justify-between items-center">
        <h4 className="filter-title">Filter</h4>
        <div className="flex justify-center items-center">
          {(activeFilters.length > 0 || dynamicFilters.length > 0) && (
            <Button className="transparent clear-btn" onClick={clearAllFilters}>
              Clear all
            </Button>
          )}
          <span className="cursor-pointer" onClick={() => setVisible(false)}>
            <CloseIcon />
          </span>
        </div>
      </div>

      <div className="custom-filters-wrapper">
        {dynamicFilters.length > 0 && (
          <div className="dynamic-filters-wrapper">
            <span className="title">All filters</span>
            {dynamicFilters.map((f, idx) => (
              <div key={idx} className="dynamic-filter flex gap-2 mb-2">
                {/* Column selector */}
                <Select
                  className="column-filter w-full"
                  options={tableHeaders}
                  value={f.column}
                  onChange={(val) => {
                    updateFilter(idx, "column", val);
                    updateFilter(idx, "condition", null);
                    updateFilter(idx, "value", []);
                  }}
                  placeholder="Select column"
                />

                {/* Condition selector */}
                {f.column && (
                  <Select
                    className="condition-filter w-full"
                    options={conditionOptionsByColumn[f.column] || []}
                    value={f.condition}
                    onChange={(val) => updateFilter(idx, "condition", val)}
                    placeholder="Condition"
                  />
                )}

                {/* Value input / select */}
                <div className="filter-value">{renderValueSelect(f, idx)}</div>

                {/* Delete button */}
                <Button
                  icon={<DeleteIcon />}
                  className="transparent delete-btn"
                  onClick={() => deleteFilter(idx)}
                />
              </div>
            ))}
          </div>
        )}

        {/* Add Filter button */}
        <div className="footer-btn-wrapper flex items-center justify-between">
          <Button
            onClick={addFilter}
            icon={<AddPrimaryIcon />}
            className="transparent add-filter-btn"
          >
            Add Filter
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <Dropdown
      overlay={menu}
      open={visible}
      onOpenChange={(val) => setVisible(val)}
      trigger={["click"]}
      placement="bottomRight"
      arrow={false}
      overlayClassName="filter-dropdown-wrapper"
    >
      <Button
        icon={<FilterIcon />}
        className={`transparent table-filter-btn ${
          activeFilterCount > 0 ? "active-filter-btn" : ""
        }`}
      >
        {activeFilterCount > 0 && (
          <span className="filter-count">{activeFilterCount}</span>
        )}
        Filter
      </Button>
    </Dropdown>
  );
};

export default FilterDropdownRoles;
